/**
 * Session 管理插件
 */

import type { Plugin } from '../../core/collector'
import type { Collector } from '../../core/collector'
import type { SessionData } from '../../@types'
import { StorageWrapper } from '../../core/storage'
import { generateUUID, isBrowser } from '../../core/utils'

/**
 * Session 管理插件类
 */
export class SessionPlugin implements Plugin {
  name = 'session'
  version = '1.0.0'

  private collector!: Collector
  private sessionData: SessionData | null = null
  private storage: StorageWrapper

  constructor() {
    this.storage = new StorageWrapper('session')
  }

  install(collector: Collector): void {
    this.collector = collector

    // 在构建埋点数据时注入 session_id
    this.injectSessionId()
  }

  /**
   * 注入 Session ID 到全局属性
   */
  private injectSessionId(): void {
    const dynamicPropsGetter = this.collector.config.get('dynamicPropsGetter')

    this.collector.config.set('dynamicPropsGetter', () => {
      const dynamicProps = dynamicPropsGetter ? dynamicPropsGetter() : {}
      return {
        ...dynamicProps,
        session_id: this.getSessionId(),
      }
    })
  }

  /**
   * 获取 Session ID
   */
  getSessionId(): string {
    if (!isBrowser()) {
      return 'non-browser-session'
    }

    const now = Date.now()
    this.sessionData = this.readSession()

    if (this.sessionData) {
      const { first_session_time, latest_session_time } = this.sessionData

      if (this.shouldCreateNewSession(now, first_session_time, latest_session_time)) {
        this.createNewSession(now)
      } else {
        this.sessionData.latest_session_time = now
        this.saveSession(this.sessionData)
      }
    } else {
      this.createNewSession(now)
    }

    return this.sessionData!.session_id
  }

  /**
   * 判断是否应该创建新 Session
   */
  private shouldCreateNewSession(
    now: number,
    firstSessionTime: number,
    latestSessionTime: number,
  ): boolean {
    const sessionConfig = this.collector.config.get('session')
    const maxSessionDuration = sessionConfig?.maxSessionDuration || 24 * 60 * 60 * 1000
    const maxIdleDuration = sessionConfig?.maxIdleDuration || 30 * 60 * 1000

    if (firstSessionTime > now || latestSessionTime > now) return true
    if (now - firstSessionTime > maxSessionDuration) return true
    if (now - latestSessionTime > maxIdleDuration) return true

    return false
  }

  /**
   * 创建新 Session
   */
  private createNewSession(now: number): void {
    const uuid = generateUUID()
    const sessionId = uuid.replace(/-/g, '')

    this.sessionData = {
      session_id: sessionId,
      first_session_time: now,
      latest_session_time: now,
    }

    this.saveSession(this.sessionData)
  }

  /**
   * 读取 Session
   */
  private readSession(): SessionData | null {
    if (!isBrowser()) return null

    try {
      const sessionConfig = this.collector.config.get('session')
      const storageKey = sessionConfig?.storageKey || 'event_tracker_session'
      const parsed = this.storage.getItem<SessionData>(storageKey)

      if (
        parsed &&
        parsed.session_id &&
        typeof parsed.first_session_time === 'number' &&
        typeof parsed.latest_session_time === 'number'
      ) {
        return parsed
      }
    } catch (error) {
      this.collector.config.warn('Session 读取失败', error)
    }
    return null
  }

  /**
   * 保存 Session
   */
  private saveSession(data: SessionData): void {
    if (!isBrowser()) return

    try {
      const sessionConfig = this.collector.config.get('session')
      const storageKey = sessionConfig?.storageKey || 'event_tracker_session'
      this.storage.setItem(storageKey, data)
    } catch (error) {
      this.collector.config.warn('Session 保存失败', error)
    }
  }

  /**
   * 清除 Session
   */
  clearSession(): void {
    this.sessionData = null
    if (!isBrowser()) return

    try {
      const sessionConfig = this.collector.config.get('session')
      const storageKey = sessionConfig?.storageKey || 'event_tracker_session'
      this.storage.removeItem(storageKey)
    } catch (error) {
      this.collector.config.warn('Session 清除失败', error)
    }
  }

  /**
   * 获取 Session 数据
   */
  getSessionData(): SessionData | null {
    return this.readSession()
  }

  destroy(): void {
    this.sessionData = null
  }
}

export default SessionPlugin
